/**
 * Edge-safe environment utilities.
 * This file contains utilities that can be used in Edge runtime (middleware).
 * Do not import Node.js-specific modules or client-only libraries here.
 */

/**
 * Checks if required Supabase environment variables are set.
 * Safe for use in Edge middleware.
 */
export const hasEnvVars =
  process.env.NEXT_PUBLIC_SUPABASE_URL &&
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

/**
 * NEXT_PUBLIC_ENVIRONMENT values:
 * - "production" (Railway production)
 * - "animo-pr-<number>" (Railway PR preview, e.g., "animo-pr-225")
 * - "development" (local)
 */

/**
 * Reads a strict boolean environment flag, failing CLOSED.
 *
 * Only an explicit `"true"` (case- and surrounding-whitespace-insensitive)
 * returns `true`. A missing variable, an empty string, or `"false"` returns
 * `false`. Any OTHER present value (`"yes"`, `"1"`, a typo like `"treu"`) is a
 * likely misconfiguration: we warn and still return `false`, because a safety
 * gate must never be enabled by a value we don't recognize. Prefer this over a
 * bare `Boolean(process.env.X)` (which is `true` for the string `"false"`) or a
 * silent `=== "true"` for anything that gates production side effects.
 *
 * Introduced for the AGC-734 morning-digest go-live switch.
 */
export function readBooleanFlag(name: string): boolean {
  const raw = process.env[name]
  if (raw === undefined) return false
  const normalized = raw.trim().toLowerCase()
  if (normalized === "true") return true
  if (normalized === "false" || normalized === "") return false
  console.warn(
    `[env] ${name} has unrecognized value ${JSON.stringify(raw)}; expected "true" or "false". Treating as false.`,
  )
  return false
}

/**
 * Checks if the app is running in a staging/preview environment.
 * Matches Railway PR preview environments (animo-pr-*).
 */
export function isStaging(): boolean {
  const env = process.env.NEXT_PUBLIC_ENVIRONMENT ?? ""
  return env.startsWith("animo-pr-")
}

/**
 * Checks if the app is running locally.
 * Returns true if NEXT_PUBLIC_ENVIRONMENT is "development" or unset.
 */
export function isLocal(): boolean {
  const env = process.env.NEXT_PUBLIC_ENVIRONMENT
  return env === "development" || env === undefined
}

/**
 * Checks if the app is running in production.
 */
export function isProduction(): boolean {
  return process.env.NEXT_PUBLIC_ENVIRONMENT === "production"
}

/**
 * Dynamically determines the app URL based on environment variables.
 * Safe for use in Edge middleware (server-side only).
 *
 * Priority:
 * 1. NEXT_PUBLIC_APP_URL (should always be set in Railway/production)
 * 2. Fallback: http://localhost:3000 (local development)
 *
 * Note: This is the Edge-safe version. For client components, use the version from lib/utils.ts
 * which includes window.location.origin fallback.
 */
export function getAppUrl(): string {
  return process.env.NEXT_PUBLIC_APP_URL ?? "http://localhost:3000"
}

/**
 * Deploy environment label for telemetry tagging.
 * Values: "production" | "animo-pr-<number>" (Railway PR preview) | "development" (local).
 * Falls back to "development" so unset/local reads as dev, matching {@link isLocal}.
 * Client-safe: RAILWAY_* is replaced with undefined in the browser bundle, so on the
 * client this resolves via NEXT_PUBLIC_ENVIRONMENT (or the "development" fallback).
 */
export function deployEnvironment(): string {
  return (
    process.env.NEXT_PUBLIC_ENVIRONMENT ??
    process.env.RAILWAY_ENVIRONMENT_NAME ??
    "development"
  )
}

/**
 * Public host/domain of the running deployment for telemetry, or null when unavailable
 * (e.g. local dev, where Railway does not inject RAILWAY_PUBLIC_DOMAIN). Server-side only.
 */
export function deployHost(): string | null {
  return process.env.RAILWAY_PUBLIC_DOMAIN ?? null
}

